import { useMemo, useState } from 'react';
import { Box, Chip, Stack } from '@mui/material';
import { GridView } from '@mui/icons-material';
import { IFeature } from '../data/types';
import { colors } from '../styles';
import FeatureCard from './FeatureCard';
import SectionHeading from './SectionHeading';
import { useHorizontalDragScroll } from '../useHorizontalDragScroll';

const ALL_TAGS = 'הכל';

interface CatalogSectionProps {
    features: IFeature[];
    onOpen: (feature: IFeature) => void;
}

const CatalogSection = ({ features, onOpen }: CatalogSectionProps) => {
    const [activeTag, setActiveTag] = useState(ALL_TAGS);
    const scroller = useHorizontalDragScroll();

    const tags = useMemo(() => {
        const unique = new Set<string>();
        features.forEach((feature) => feature.tags.forEach((tag) => unique.add(tag)));
        return [ALL_TAGS, ...Array.from(unique)];
    }, [features]);

    const shown = useMemo(() => (activeTag === ALL_TAGS ? features : features.filter((f) => f.tags.includes(activeTag))), [
        features,
        activeTag
    ]);

    if (!features.length) return null;

    return (
        <Box component="section" dir="rtl" sx={{ width: '100%', maxWidth: '100%', minWidth: 0, overflow: 'hidden' }}>
            <SectionHeading
                title="כל היכולות"
                subtitle="כל מה שאפשר להפעיל בניהול קו — מסוננות לפי תחום. גוררים הצידה כדי לראות עוד."
                icon={<GridView sx={{ color: '#1264e2', fontSize: 30 }} />}
                action={
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {tags.map((tag) => (
                            <Chip
                                key={tag}
                                label={tag}
                                onClick={() => setActiveTag(tag)}
                                variant={activeTag === tag ? 'filled' : 'outlined'}
                                sx={{
                                    fontWeight: 800,
                                    ...(activeTag === tag
                                        ? { bgcolor: colors.ink, color: '#ffffff' }
                                        : { color: colors.body, borderColor: colors.border })
                                }}
                            />
                        ))}
                    </Stack>
                }
            />
            <Box
                ref={scroller.ref}
                dir="rtl"
                onPointerDownCapture={scroller.onPointerDownCapture}
                onPointerMove={scroller.onPointerMove}
                onPointerUp={scroller.onPointerUp}
                onPointerCancel={scroller.onPointerCancel}
                onClickCapture={scroller.onClickCapture}
                sx={{
                    display: 'grid',
                    gridAutoFlow: 'column',
                    gridTemplateRows: 'repeat(2, minmax(0, 1fr))',
                    gridAutoColumns: {
                        xs: 'minmax(260px, 78vw)',
                        sm: '320px',
                        md: '360px'
                    },
                    gap: 3,
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    pb: 1.5,
                    mx: { xs: -1, sm: 0 },
                    px: { xs: 1, sm: 0.5 },
                    scrollSnapType: scroller.dragging ? 'none' : 'x proximity',
                    overscrollBehaviorX: 'contain',
                    WebkitOverflowScrolling: 'touch',
                    touchAction: 'pan-x',
                    cursor: scroller.dragging ? 'grabbing' : 'grab',
                    userSelect: 'none',
                    scrollbarWidth: 'thin',
                    '&::-webkit-scrollbar': { height: 10 },
                    '&::-webkit-scrollbar-thumb': {
                        bgcolor: 'rgba(15, 50, 80, 0.35)',
                        borderRadius: 8
                    },
                    '& img': { WebkitUserDrag: 'none', userSelect: 'none' },
                    '& > *': {
                        scrollSnapAlign: 'start',
                        minWidth: 0,
                        height: '100%'
                    }
                }}
            >
                {shown.map((feature) => (
                    <FeatureCard key={feature.id} feature={feature} variant="catalog" onOpen={onOpen} />
                ))}
            </Box>
        </Box>
    );
};

export default CatalogSection;
